<template>
  <section class="pd-details">
    <article class="view">
      <form>
        <div class="form-row">
          <label for="bib">Bib</label>
          <input type="text" autofocus
            v-model="model.bib"
            ref="bibInput"
            @blur="attrChangeDidFinish('bib')"
            :class="{bib: true, error: bibIsDuplicate}"
            name="bib"
            placeholder="Bib"
          >
          <span v-if="bibIsDuplicate" class="note error">
            Bib is Duplicate {{ bibIsDuplicate.lastName }}, {{ bibIsDuplicate.firstName }}
          </span>
        </div>


        <div class="form-row">
          <label for="first-name">Name</label>
          <input type="text"
            v-model="model.firstName"
            @blur="attrChangeDidFinish('firstName')"
            name="first-name"
            autocomplete="given-name"
            placeholder="First Name"
          >
          <input type="text"
            v-model="model.lastName"
            @blur="attrChangeDidFinish('lastName')"
            name="last-name"
            autocomplete="family-name"
            placeholder="Last Name"
          >
        </div>
    
        <div class="form-row" id="race-row">
          <label for="race_id">Race</label>

          <popover-inline
            :title="model.race?.name || 'Race Unset'"
            class="race-popover"
            :button-class="hasSelectedRace ? 'select' : 'select error'"
            prefer="rt"
          >
            <menu-contents v-model="model.raceId" :menus="races.map(r=> ({value: r.id, title: r.name, iconColor:r.color}))" />
          </popover-inline>
      
          <span class="st">Race Start: {{ $fmt.full(model.race?.startTime, raceEvent.timezoneOffset) }}</span>
        </div>
        
        <div class="form-row">
          <label for="wave-start-time">Start Time</label>

          <datetime-picker id="wave-start-time" v-model="model.waveStartTime" @blur="attrChangeDidFinish('waveStartTime')" />
          <popover-inline title="Set" button-class="select" prefer="rt" >
            <menu-contents v-model="model.waveStartTime" :menus="startTimeMenus" @change="attrChangeDidFinish('waveStartTime')"/>
          </popover-inline>
        </div>
    
        <div class="form-row">
          <label for="name">Gender</label>
          <input
            v-model="model.gender"
            type="text"
            name="name"
            :class="genderClass"
            limit="1"
            autocomplete="sex"
            @keydown="genderKeyDown"
            @blur="genderBlur"
          >
          <span class="debug">{{ model.gender }} {{ genderIsIntentional ? '(intentional)' : '' }} (guessed: {{ genderGuess }})</span>
        </div>
    
        <div class="form-row debug">
          <label for="name">Birthday</label>
          <span class="note">{{ String(model.dob) }}</span>
        </div>
        <div class="form-row">
          <label for="name">Birthday</label>
          <date-input
            v-model="model.dob"
            name="dob"
            @blur="attrChangeDidFinish('dob')"
          />
          <label for="age">Age</label>
          <input
            v-model.lazy="model.age"
            @blur="attrChangeDidFinish('age')"
            type="text"
            name="age"
            class="single"
          >
        </div>
    
        <div class="form-row">
          <label for="city">City</label>
          <input
            v-model="model.city"
            @blur="attrChangeDidFinish('city')"
            type="text"
            name="city"
          >
          <label for="state">State</label>
          <input
            v-model="model.state"
            @blur="attrChangeDidFinish('state')"
            type="text"
            name="state"
            class="single"
          >
        </div>

        <div class="form-row chips">
          <label for="chips">Chips</label>
          <div class="chips-wrap">
            <ul class="box naked">
              <li v-for="(chip, i) in chips" :key="`${chip || 'chip'}-${i}`">
                <autocomplete-row
                  :model-value="chip"
                  :choices="chipChoices"
                  row-class="chip-row"
                  @update:model-value="setChip(i, $event)"
                  @change="chipDidChange"
                >
                </autocomplete-row>
                <button class="mini bar del" @click.prevent="removeChip(i)">Remove</button>
              </li>
            </ul>
            <div class="control">
              <button class="mini add" @click.prevent="addChip">Add</button>
            </div>
          </div>
        </div>
    
    
        <div class="form-row debug">
          {{ Math.random() }}
        </div>      
        
      </form>
    </article>
  </section>
</template>

<style lang="scss">
  .pd-details {
    margin: 1rem 1rem 0 0;
    
    #race-row .st { margin-left: 1rem; color: var(--text-dim1-color);}
    #race-row .race-popover button.error {
      border-color: var(--error-color);
    }
    
    .debug { display: none; }
    
    input[type=text].gender          { text-align: center; }
    input[type=text].gender-ok       { background: var(--success-color); color: #000; }
    input[type=text].gender-override { background: var(--button-danger-bgcolor); color: #000; }
    
    .chips {
      ul {
        width: min-content;
        li { 
          text-align: left; 
          display: flex; 
          align-items: center;
        }
        input {
          width: 29ex;
          max-width: initial !important;
          margin-left: 0;
        }
      }
      .control {
        display: block;
        margin-left: 0.65rem;
      }
    }
  }
  
  // Must be outside pd-details
  .race-popover .menu .icon {
    background: var(--color);
    height: 1rem;
    width: 1rem;
    border-radius: 50%;
    vertical-align: middle;
    margin-right: 0.5ex;
  }
</style>

<script>
  import { nextTick } from 'vue'
  import AutocompleteRow from '@/components/autocomplete-row.vue'
  import BibRangeParser from "@/lib/bib-range-parser.js"
  import NameChecker from "@/lib/name-checker.js"
  import eventbus from '@/eventbus.js'

  function raceGuess(races, bib) {
    if (!races?.find) return null;
    return races.find(r => {
      let rangeParser = new BibRangeParser(r.bibRanges);
      if (!rangeParser.parseRanges()) {
        // This race's ranges are broken.
        return false;
      }
      
      const numbers = rangeParser.parsedNumbers.map(x=>x.toString());
      return numbers.indexOf(bib) >= 0;
    });
  }

  export default {
    name: 'PdDetails',

    components: {
      AutocompleteRow,
    },
  
    inject: [
      'activeRaceEvent',          // app
      'commitChangedAttributes',  // participant-details
      'races',                    // participant-details
      'raceEvent',                // participant-details
    ],
  
    props: {
      model: { // ModelEditor Proxy of a participant
        type: Object,
        required: true,
      }
    },
  
    data() {
      return {
        raceIsIntentional: false,    // auto-assign these things if they're not pre set.
        raceGuess: undefined,

        genderIsIntentional: false,  // auto-assign these things if they're not pre set.
        genderGuess: undefined,
      }
    },
  
    computed: {

      bibIsDuplicate() {
        return false
      },
  
      bibRaceGuess(){
        return raceGuess(this.races, this.model.bib);
      },

      chips() {
        return Array.isArray(this.model.chips) ? this.model.chips : [];
      },

      chipChoices() {
        return Array.isArray(this.model.race?.prefixes) ? this.model.race.prefixes : [];
      },

      hasSelectedRace() {
        const raceId = this.model.raceId ?? this.model.race?.id;
        const normalizedRaceId = (raceId && typeof raceId === 'object') ? raceId.id : raceId;
        return normalizedRaceId !== undefined && normalizedRaceId !== null && normalizedRaceId !== '';
      },
      
      genderMatchesGuess() {
        // Check entered gender against the guess from the database.
        //
        if (!this.genderGuess || !this.model.gender) return;
        return this.genderGuess.toUpperCase() == this.model.gender.toUpperCase();
      },
      
      genderClass() {
        const m = this.genderMatchesGuess ? 'gender-ok' : 'gender-override';
        return [
          'single',  // this is a single character field
          'gender',  // this is the gender field
          m          // gender-ok/gender-override
        ];
      },
      
      startTimeMenus() {
        let waves = this.model.race?.waves?.waves ?? [];
        const menus = waves.map(w=> ({value: new Date(w.time), title: `Wave ${w.id} - ${this.$fmt.daytime(w.time)}` }));

        if (menus.length  < 1) {
          menus.push({title: 'No Waves Configured', disabled: true})
        }
        menus.push({ type: 'separator'});

        const waveStartTime = this.model.waveStartTime;
        if (waveStartTime !== undefined && waveStartTime !== null) {
          const selectedTime = new Date(waveStartTime).getTime();
          const selectedIsWave = menus.some(m => m.value.getTime() === selectedTime);
          if (!selectedIsWave) {
            menus.push({
              value: waveStartTime,
              title: `Custom`
            });
          }
        }
        
        menus.push({ value: null, title: 'Unset' })

        return menus;
      }
    },
    
    watch: {
      'model.firstName': function(val) {
        this.genderDoGuess(val);
      },
    },
  
    mounted() {
      NameChecker.init(); // Prepare gender guesser
      
      this.raceIsIntentional   = this.model.raceId !== undefined;
      this.genderIsIntentional = this.model.gender !== undefined;
      this.genderDoGuess(this.model.firstName);
      
      nextTick(() => this.$refs.bibInput?.focus());
      // eventbus.on('tick', () => console.log("tick", this.$forceUpdate()) )
    },
  
    methods: {
      setChip(index, value) {
        const chips = [...this.chips];
        chips[index] = value;
        this.model.chips = chips;
      },

      addChip() {
        this.model.chips = [...this.chips, ''];
      },

      removeChip(index) {
        this.chips.splice(index, 1);
        this.commitChangedAttributes();
      },

      chipDidChange() {
        if (this.model._changedAttributes.has('chips')) {
          this.commitChangedAttributes();
        }
      },

      attrChangeDidFinish(attr) {
        if (!this.model._changedAttributes.has(attr)) {
          console.debug(`⚠️ Ignoring unchanged attr blur on '${attr}'`);
          return; // This is just a blur without an edit
        }
        
        console.debug(`pd-details: attrChangeDidFinish ${attr}`)
        if ('bib' == attr && !this.raceIsIntentional) {
          // Auto-assign a race for new participants
          let r = this.bibRaceGuess;
          if (r) {
            this.model.raceId = r;
          }
        }
      
        this.commitChangedAttributes();
      },
    
      raceIntentionallyChanged() {
        // Race assignements from bib range assumptions
        // are not intentional
        this.raceIsIntentional = true;
      },
      
      genderDoGuess(name) {
        if (!name)
          return;
        
        NameChecker.checkName(name.toLowerCase()).then((g) => {
          if (g) g = g.toUpperCase();
          
          this.genderGuess = g;

          if (!this.genderIsIntentional && g != 'X') {
            this.model.gender = g;
          }
          else {
            console.debug("Gender is intentionally set; not applying guess", g);
          }
        });
      },
    
      genderKeyDown(e) {
        if (e.code == 'Tab')
          return
        
        e.preventDefault();

        if (e.code == 'KeyM' || e.code == 'KeyF' || e.code == 'KeyX')  {
          this.genderIsIntentional = true;
          this.model.gender = e.key.toUpperCase();
          e.target.value = this.model.gender;
        }
        else
        if (e.code == 'Backspace' || e.code == 'Delete') {
          this.genderIsIntentional = false;
          this.model.gender = '';
          e.target.value = ''
        }
        else {
          // ignore
          return;
        }

        // this.model._commitValues();
        this.commitChangedAttributes();
      },
      
      genderBlur(e) {
      },
    }
  
  }
</script>
